import React, { FC, useCallback, useState, useEffect } from 'react';
import {
  Button,
  DynamicPagination,
  FlexGrid,
  Sizes,
  TextBoldness,
  Typography,
  VerticalSpacing,
  VerticalSpacingSizes,
  getClassNameFactory,
} from '@dcl/ui';
import './SupportHoursMobileTable.css';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import {
  ERROR_MESSAGE,
  INITIAL_PAGE_NUMBER,
  MAX_VISIBLE_PAGES,
  NEXT_LABEL,
  PAGINATION_LABEL,
  PREVIOUS_LABEL,
  RESULTS_PER_PAGE_LABEL,
  ROWS_PER_PAGE_LABEL_CONTENT,
  PAGE_LABEL,
  FILTER_BUTTON_TEXT_ARIA_LABEL,
  FILTER_BUTTON_TEXT,
} from '../../constants';
import useSupportHourMobilePagination from '../hooks/useSupportHourMobilePagination';
import {
  ButtonColor,
  ButtonKind,
  IconPosition,
} from '@dcl/ui/dist/elements/Button';
import FilterIcon from '../../icons/FilterIcon';
import { FilterModalSupportHours } from './components/FilterModalSupportHours/FilterModalSupportHours';
import { ISupportHoursMobileTable, IResultData } from '../../SupportHoursTypes';
import AlertIcon from '../../icons/AlertIcon';
import { filterData, transformFilterData } from '../../helper';

export const SupportHoursMobileTable: FC<ISupportHoursMobileTable> = (
  props
) => {
  const { columns, data, internalUseText, isLoggedIn = false } = props;
  const [currentPage, setCurrentPage] = useState<number>(INITIAL_PAGE_NUMBER);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getClassName = getClassNameFactory(SupportHoursMobileTable.displayName);
  const [mobileTableBodyData, setMobileTableBodyData] = useState<any[]>([]);
  const [filterOptions, setFilterOptions] = useState<IResultData[]>([]);
  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const [filteredTableData, setFilteredTableData] = useState<object[]>([]);

  useEffect(() => {
    const updatedData = data;
    setFilteredTableData(updatedData);
  }, [data]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  useEffect(() => {
    setCurrentPage(INITIAL_PAGE_NUMBER);
    const transformedFilterData = transformFilterData(columns, data);
    setFilterOptions(transformedFilterData);
  }, [data, columns]);

  const columnHeaders = columns;

  const {
    mobileViewPageSize,
    totalPages,
    ROWS_PER_PAGE,
    mobileViewData,
    handlePageChange,
    handlePageSizeChange,
    totalItems,
    pageIndex,
  } = useSupportHourMobilePagination(filteredTableData as []);

  const tableBodyData = mobileViewData;

  useEffect(() => {
    setMobileTableBodyData(tableBodyData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mobileViewData]);

  const handlePageChangeClick = useCallback(
    (newPage: number) => {
      handlePageChange(newPage);
      setCurrentPage(newPage);
    },
    [handlePageChange]
  );

  const handlePageSizeChangeClick = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize);
      handlePageSizeChange(newPageSize);
    },
    [handlePageSizeChange]
  );

  const renderPagination = useCallback(
    () => (
      <DynamicPagination
        previousButtonLabel={PREVIOUS_LABEL}
        nextButtonLabel={NEXT_LABEL}
        paginationLabel={PAGINATION_LABEL}
        rowsPerPageLabel={RESULTS_PER_PAGE_LABEL}
        rowsPerPageLabelContent={ROWS_PER_PAGE_LABEL_CONTENT}
        pageSize={mobileViewPageSize}
        viewingLabel={PAGE_LABEL}
        goToPageError={ERROR_MESSAGE}
        totalPages={totalPages}
        totalItems={totalItems}
        startFromPage={currentPage}
        onPageChange={handlePageChangeClick}
        onPageSizeChange={handlePageSizeChangeClick}
        numberOfVisiblePages={MAX_VISIBLE_PAGES}
        rowsPerPageValues={ROWS_PER_PAGE}
      />
    ),
    [
      handlePageChangeClick,
      handlePageSizeChangeClick,
      currentPage,
      mobileViewPageSize,
      ROWS_PER_PAGE,
      totalItems,
      totalPages,
    ]
  );

  const paginationText = `(Showing ${
    currentPageSize * pageIndex - currentPageSize + 1
  } to ${
    currentPageSize * pageIndex > totalItems
      ? totalItems
      : currentPageSize * pageIndex
  } of ${totalItems})`;

  const applyFilter = (options: IResultData[]) => {
    setFilterOptions(options);
    const { filteredData } = filterData(data, options);
    setFilteredTableData(filteredData);
    setCurrentPage(INITIAL_PAGE_NUMBER);
  };

  const getInternalRowModifier = () => 'internalRow';

  const getLightRowModifier = () => 'light';

  const getDarkRowModifier = () => 'dark';

  const determineRowModifier = (isInternal: boolean, index: number) => {
    if (isInternal) {
      return getInternalRowModifier();
    }
    return index % 2 === 0 ? getLightRowModifier() : getDarkRowModifier();
  };

  return (
    <div className={getClassName()} data-testid="mobileViewContainer">
      <hr className={getClassName({ descendantName: 'line' })} />

      <FlexGrid>
        <FlexGrid.Row className={getClassName({ descendantName: 'flexRow' })}>
          {filterOptions.length > 0 && (
            <FlexGrid.Col
              lg={12}
              sm={12}
              className={getClassName({ descendantName: 'filterBox' })}
            >
              <Button
                block
                kind={ButtonKind.BUTTON}
                color={ButtonColor.SECONDARY}
                data-testid="FileDownloadExperience-FilterIcon"
                iconPosition={IconPosition?.LEFT}
                icon={<FilterIcon />}
                aria-label={FILTER_BUTTON_TEXT_ARIA_LABEL}
                onClick={() => toggleModal()}
              >
                {FILTER_BUTTON_TEXT}
              </Button>
            </FlexGrid.Col>
          )}
          <FlexGrid.Col
            lg={12}
            sm={12}
            className={getClassName({ descendantName: 'filterBox' })}
          >
            <Typography
              className={getClassName({ descendantName: 'paginationText' })}
              type={Sizes.XXTRASMALL}
              tag={TypographyTags.SPAN}
            >
              {mobileTableBodyData.length > 0 && paginationText}
            </Typography>
            {internalUseText && isLoggedIn && (
              <div
                className={getClassName({ descendantName: 'internalAlert' })}
              >
                <div
                  className={getClassName({
                    descendantName: 'internalAlertIcon',
                  })}
                >
                  {AlertIcon}
                </div>
                <div
                  className={getClassName({
                    descendantName: 'internalAlertText',
                  })}
                >
                  {internalUseText}
                </div>
              </div>
            )}
          </FlexGrid.Col>
        </FlexGrid.Row>
      </FlexGrid>

      <div className={getClassName({ descendantName: 'container' })}>
        {mobileTableBodyData.map((rowData: any, index) => {
          const isInternalRow = rowData.internal === 'true';
          const rowKey = `header_${index + 1}`;
          const rowModifier = determineRowModifier(isInternalRow, index);
          return (
            <div
              className={getClassName({
                descendantName: 'background',
                modifiers: rowModifier,
              })}
              key={rowKey}
            >
              {isInternalRow && (
                <div
                  key={rowKey}
                  className={getClassName({ descendantName: 'alertIcon' })}
                >
                  {AlertIcon}{' '}
                </div>
              )}
              {columnHeaders.map((column) => {
                const isColumnInternal = !isInternalRow && column.internal;
                const columnKey = column.accessor!.toString();
                const originalColumnData = rowData['original' + columnKey];
                const currentColumnData = rowData[columnKey];
                const isComplexData =
                  typeof originalColumnData === 'object' ||
                  typeof currentColumnData === 'object';
                const displayColumnData =
                  originalColumnData || currentColumnData;
                const columnWidth = isColumnInternal ? 5 : 4;
                return (
                  <VerticalSpacing
                    size={VerticalSpacingSizes.S}
                    key={columnKey}
                  >
                    <FlexGrid.Row
                      className={getClassName({
                        descendantName: 'flexRow',
                        modifiers: isColumnInternal && 'internal',
                      })}
                    >
                      <FlexGrid.Col
                        xs={columnWidth}
                        sm={columnWidth}
                        md={columnWidth}
                        className={getClassName({
                          descendantName: 'headerGrid',
                        })}
                      >
                        <Typography
                          className={getClassName({
                            descendantName: 'heading',
                          })}
                          type={Sizes.XXTRASMALL}
                          tag={TypographyTags.SPAN}
                        >
                          {!isInternalRow && column.internal && (
                            <div
                              className={getClassName({
                                descendantName: 'alertIconColumn',
                              })}
                            >
                              {AlertIcon}
                            </div>
                          )}
                          {column.Header !== 'Action' && column.Header + ': '}
                        </Typography>
                      </FlexGrid.Col>
                      <FlexGrid.Col
                        xs={isColumnInternal ? 7 : 8}
                        sm={isColumnInternal ? 7 : 8}
                        md={isColumnInternal ? 7 : 8}
                        className={getClassName({ descendantName: 'bodyGrid' })}
                      >
                        {isComplexData ? (
                          displayColumnData
                        ) : (
                          <Typography
                            className={getClassName({
                              descendantName: 'bodyText',
                            })}
                            type={Sizes.XXTRASMALL}
                            tag={TypographyTags.SPAN}
                            boldness={TextBoldness.Regular}
                          >
                            {rowData['original' + columnKey] ||
                              rowData[columnKey]}
                          </Typography>
                        )}
                      </FlexGrid.Col>
                    </FlexGrid.Row>
                  </VerticalSpacing>
                );
              })}
            </div>
          );
        })}
        {renderPagination()}
      </div>
      {isModalOpen && (
        <FilterModalSupportHours
          handleCloseModal={toggleModal}
          resultData={filterOptions}
          handleAppliedFilter={applyFilter}
        />
      )}
    </div>
  );
};

SupportHoursMobileTable.displayName = 'SupportHoursMobileTable';
